/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import { useSnackbar } from 'src/components/snackbar';
import { useBoolean } from 'src/hooks/use-boolean';
import { useAxiosClientContext } from 'src/http/context/axios-client-context';
import { PromotionsRepository } from 'src/repositories/promotions/promotions.repository';
import { useFilterStore } from 'src/stores/filters/filters.store';

export const usePromotions = () => {
  const { order, orderBy, page, limit, total, filters, setOrder, setTotal, setOrderBy, setLimit, setPage, resetFilters } = useFilterStore(
    (state) => state
  );
  const api = useAxiosClientContext();
  const [rowToDelete, setRowToDelete] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  const [promotions, setPromotions] = useState<any[]>([]);
  const [checked, setChecked] = useState<string[]>([]);
  const { toastWarning } = useSnackbar();
  const useConfirmDeleteDialog = useBoolean(false);
  const repository = new PromotionsRepository(api);

  const tableHeader = [
    { label: 'Ativar', id: 'status', sortable: true },
    { label: 'Promoções e Campanhas', id: 'promotionName', sortable: true },
    { label: 'Data inicial - Data Final', id: 'date', sortable: true },
    { label: 'Imagem', id: 'banner' },
    { label: 'Texto', id: 'preview', sortable: true },
  ];

  const getData = async () => {
    try {
      setIsLoading(true);
      const response = await repository.getPromotions({
        order,
        orderBy,
        page: page + 1,
        limit,
        ...filters,
      });
      setPromotions(response.value.data.map((res: any) => ({ ...res, id: res.id })));
      setTotal(response.value.total);
      setChecked(response.value.data.filter((r: any) => r.isActive === true).map((r: any) => r.id));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckRow = async (id: string) => {
    const response = await repository.changeStatus(id);
    if (response.isLeft()) {
      toastWarning('Erro ao alterar status!');
    }
    if (checked.includes(id)) {
      setChecked(checked.filter((c) => c !== id));
    } else {
      setChecked([...checked, id]);
    }
  };

  const createSortHandler = (id: string) => {
    if (orderBy === id) {
      if (order === 'asc') {
        setOrder('desc');
      } else {
        setOrder('asc');
      }
    } else if (order !== id) {
      setOrderBy(id);
      setOrder('asc');
    }
  };

  const handleChangePage = (row: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (row: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setLimit(parseInt(row.target.value, 10));
    setPage(0);
  };

  const handleAddNew = () => {
    setPromotions([
      {
        id: `-`,
        promotionName: '',
        startDate: '',
        endDate: '',
        preview: '',
        regulationUrl: '',
        banner: '',
        thumbnail: { url: '', name: '', type: '' },
        isActive: true,
      },
      ...promotions,
    ]);
    setChecked([`-`, ...checked]);
  };

  const handleDelete = (id: string) => {
    useConfirmDeleteDialog.onTrue();
    setRowToDelete(id);
  };

  const handleConfirmDelete = async () => {
    useConfirmDeleteDialog.onFalse();
    if (rowToDelete.name !== '') {
      const response = await repository.delete(rowToDelete.id);
      if (response.isLeft()) {
        toastWarning(response.value.message);
      } else {
        toastWarning('Promoção excluída com sucesso.');
        getData();
      }
    } else {
      setPromotions(promotions.filter((r) => r.id !== rowToDelete.id));
    }
  };

  useEffect(() => {
    getData();
    return () => {
      resetFilters();
    };
  }, []);

  useEffect(() => {
    getData();
  }, [order, orderBy, page, limit]);

  return {
    isLoading,
    order,
    orderBy,
    page,
    limit,
    total,
    tableHeader,
    checked,
    promotions,
    useConfirmDeleteDialog,
    createSortHandler,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCheckRow,
    handleDelete,
    handleConfirmDelete,
    getData,
    handleAddNew,
  };
};
